/**
 * Sport capabilities — single source of truth condivisa fra apps/frontend,
 * apps/backend e apps/mobile.
 *
 * File JS puro (no build step) + tipi in sport-capabilities.d.ts → utilizzabile
 * da CRA, Nest, Metro/Expo senza configurazione bundler aggiuntiva.
 *
 * Aggiungere qui ogni regola sport-specifica che dipende dalle caratteristiche
 * del gioco (ruoli, regole, formazione) invece di hard-codare nei singoli
 * componenti. La chiave è il valore di `sport` come restituito da
 * `useSessionTaxonomy` / `SPACE_TO_SPORT`, che usa underscore (es. `rugby_union`).
 */

const ROLE_LABEL = (key, label) => ({ key, label });

const ROLES_BY_SPORT = {
  soccer: [
    ROLE_LABEL('goalkeeper', 'Portiere'),
    ROLE_LABEL('defender', 'Difensore'),
    ROLE_LABEL('midfielder', 'Centrocampista'),
    ROLE_LABEL('winger', 'Esterno'),
    ROLE_LABEL('forward', 'Attaccante'),
  ],
  rugby_union: [
    ROLE_LABEL('prop', 'Pilone'),
    ROLE_LABEL('hooker', 'Tallonatore'),
    ROLE_LABEL('lock', 'Seconda linea'),
    ROLE_LABEL('flanker', 'Flanker'),
    ROLE_LABEL('number-8', 'Numero 8'),
    ROLE_LABEL('scrum-half', 'Mediano di mischia'),
    ROLE_LABEL('center', 'Centro'),
    ROLE_LABEL('right-wing', 'Ala'),
    ROLE_LABEL('fullback', 'Estremo'),
  ],
  basketball: [
    ROLE_LABEL('point-guard', 'Playmaker'),
    ROLE_LABEL('shooting-guard', 'Guardia'),
    ROLE_LABEL('small-forward', 'Ala piccola'),
    ROLE_LABEL('power-forward', 'Ala grande'),
    ROLE_LABEL('center', 'Centro'),
  ],
  volleyball: [
    ROLE_LABEL('setter', 'Palleggiatore'),
    ROLE_LABEL('opposite-hitter', 'Opposto'),
    ROLE_LABEL('outside-hitter', 'Schiacciatore'),
    ROLE_LABEL('middle-blocker', 'Centrale'),
    ROLE_LABEL('libero', 'Libero'),
  ],
  athletics: [],
  gym: [],
};

const DEFAULT_CAPABILITIES = {
  hasGoalkeepers: false,
  roles: [],
};

const SPORT_CAPABILITIES = {
  soccer: { hasGoalkeepers: true, roles: ROLES_BY_SPORT.soccer },
  rugby_union: { hasGoalkeepers: false, roles: ROLES_BY_SPORT.rugby_union },
  basketball: { hasGoalkeepers: false, roles: ROLES_BY_SPORT.basketball },
  volleyball: { hasGoalkeepers: false, roles: ROLES_BY_SPORT.volleyball },
  athletics: { hasGoalkeepers: false, roles: [] },
  gym: { hasGoalkeepers: false, roles: [] },
};

/**
 * Restituisce le capabilities per uno sport. Sport sconosciuto / null →
 * defaults conservativi (tutto false, nessun ruolo) per non far comparire
 * feature non supportate accidentalmente.
 *
 * @param {string|null|undefined} sport
 * @returns {{ hasGoalkeepers: boolean, roles: Array<{ key: string, label: string }> }}
 */
function getSportCapabilities(sport) {
  if (!sport) return DEFAULT_CAPABILITIES;
  return SPORT_CAPABILITIES[sport] || DEFAULT_CAPABILITIES;
}

/**
 * Restituisce la label localizzata IT di un ruolo dato lo sport.
 * Se il ruolo non è registrato, ritorna la chiave (capitalizzata).
 *
 * @param {string|null|undefined} sport
 * @param {string|null|undefined} roleKey
 * @returns {string}
 */
function getRoleLabel(sport, roleKey) {
  if (!roleKey) return '';
  const caps = getSportCapabilities(sport);
  const found = caps.roles.find((r) => r.key === roleKey);
  if (found) return found.label;
  return roleKey.charAt(0).toUpperCase() + roleKey.slice(1);
}

module.exports = {
  SPORT_CAPABILITIES,
  getSportCapabilities,
  getRoleLabel,
};
